import store from '@/store/'
import { numberFormatAddComma } from '@/utils/commonMethods'

/* --------------------------------------------------
 * URIからAPI用のクエリーを生成する処理
 -------------------------------------------------- */

interface SearchParamsType {
  name: string
  type: string
}
interface Master {
  id: string
  opt_id?: string
  option_id?: string
  option_name: string
}

interface SearchParams {
  [key: string]: SearchParamsType
}

// 病院用パラメータ定義
const paramsObjHospital: SearchParams = {
  pr: {
    name: 'prefectures',
    type: 'array'
  },
  cl: {
    name: 'hospital_class',
    type: 'array'
  },
  dh: {
    name: 'designated_hospital',
    type: 'array'
  },
  kw: {
    name: 'discerning_conditions',
    type: 'array'
  },
  rb: {
    name: 'bonus',
    type: 'boolean'
  },
  ro: {
    name: 'overtime_hours_mavg',
    type: 'boolean'
  },
  rh: {
    name: 'house',
    type: 'boolean'
  },
  gs: {
    name: 'j_graduating_school',
    type: 'boolean'
  },
  bn: {
    name: 'bed_num',
    type: 'range'
  },
  dn: {
    name: 'doctor_num',
    type: 'range'
  },
  pn: {
    name: 'positions_num',
    type: 'range'
  },
  ai: {
    name: 'income',
    type: 'range'
  },
  sn: {
    name: 'shifts_num',
    type: 'range'
  },
  fw: {
    name: 'free_word',
    type: 'string'
  },
  pg: {
    name: 'page_no',
    type: 'string'
  }
}
// 専門研修用パラメータ定義
const paramsObjLatter: SearchParams = {
  pr: {
    name: 'prefectures',
    type: 'array'
  },
  sj: {
    name: 'program_subjects',
    type: 'array'
  },
  cl: {
    name: 'hospital_class',
    type: 'array'
  },
  pk: {
    name: 'feature_tag',
    type: 'array'
  },
  dh: {
    name: 'designated_hospital',
    type: 'array'
  },
  kw: {
    name: 'discerning_conditions',
    type: 'array'
  },
  bn: {
    name: 'bed_num',
    type: 'range'
  },
  sb: {
    name: 'department_bed_num',
    type: 'range'
  },
  mi: {
    name: 'resident_num',
    type: 'range'
  },
  si: {
    name: 'instructor_num',
    type: 'range'
  },
  dn: {
    name: 'doctor_num',
    type: 'range'
  },
  pn: {
    name: 'recruitment_num',
    type: 'range'
  },
  ai: {
    name: 's_salary3_num',
    type: 'range'
  },
  sn: {
    name: 's_shifts3_num',
    type: 'range'
  },
  fw: {
    name: 'free_word',
    type: 'string'
  },
  pg: {
    name: 'page_no',
    type: 'string'
  }
}

/**
 * URLパラメータを検索用のパラメータに変換する
 * @param key - URLパラメータの接頭辞
 * @param param - URLパラメータ
 * @param type - 検索用のパラメータの型
 * @param paramsObj - 病院用か専門研修用のパラメータ定義
 * @return - 検索用のパラメータオブジェクト
 */
const getParams = (
  key: string,
  param: string,
  type: string,
  paramsObj: SearchParams
) => {
  const value = param.replace(`${key}_`, '')

  if (type === 'string') {
    return [
      {
        key: paramsObj[key].name,
        value: decodeURIComponent(value)
      }
    ]
  }

  if (type === 'boolean') {
    return value !== '1'
      ? []
      : [
          {
            key: paramsObj[key].name,
            value: true
          }
        ]
  }

  if (type === 'array') {
    return [
      {
        key: paramsObj[key].name,
        value: value.split('+')
      }
    ]
  }

  if (type === 'range') {
    const valueArr = value.split('-')

    return valueArr.length > 2
      ? []
      : [
          {
            key: `${paramsObj[key].name}_lo`,
            value: valueArr[0]
          },
          {
            key: `${paramsObj[key].name}_hi`,
            value: valueArr[1]
          }
        ]
  }

  return []
}

/**
 * URLパラメータから検索用のパラメータを取得する
 * @param params - URLパラメータ
 * @param type - 病院検索か専門研修検索か
 * @return - 検索用のパラメータオブジェクト全体
 */
export function getSearchQuery(
  params: string,
  type: 'hospital' | 'latter' = 'hospital'
) {
  const paramsArr = params.split('/')
  const paramsObj = type === 'hospital' ? paramsObjHospital : paramsObjLatter
  const resObj: any = {}

  paramsArr.forEach((param: string) => {
    if (!param) return
    // 先頭2文字を取得
    const key = param.substring(0, 2)
    // 取得した2文字がオブジェクトのキーとして存在しなかったらreturn
    if (!paramsObj[key]) return
    // 検索用パラメータを取得してオブジェクト全体に追加
    const paramItem = getParams(key, param, paramsObj[key].type, paramsObj)
    paramItem.forEach((item: any) => (resObj[item.key] = item.value))
  })

  return resObj
}

export function setSearchResultTitle(
  params: string,
  type: 'hospital' | 'latter' = 'hospital'
) {
  const baseTitle = type === 'hospital' ? '研修先病院' : '専門研修プログラム'
  const query = getSearchQuery(params, type)
  const titleArr = []
  let conditionsCount = 0
  let areaCondition = ''

  // ページNoは除外
  if (query.page_no) delete query.page_no

  // 検索条件がない
  if (Object.keys(query).length === 0) {
    return {
      title: '全国の' + baseTitle
    }
  }

  // 共通：病院所在地
  if (query.prefectures && query.prefectures.length > 0) {
    const items = store.state.master.prefectures.filter((el: Master) => {
      return query.prefectures.includes(el.opt_id)
    })

    areaCondition = items.map((el: Master) => el.option_name).join('、')
    titleArr.push(areaCondition)
    conditionsCount++
  }
  // 専門研修：専門研修プログラム科目
  if (query.program_subjects?.length > 0) {
    titleArr.push(
      store.state.master.programSubjects
        .filter((el: Master) => query.program_subjects.includes(el.opt_id))
        .map((el: Master) => el.option_name)
        .join('、')
    )
    conditionsCount++
  }
  // 共通：病院種別
  if (query.hospital_class && query.hospital_class.length > 0) {
    const items = store.state.master.hospitalClass.filter((el: Master) => {
      return query.hospital_class.includes(el.opt_id)
    })

    titleArr.push(items.map((el: Master) => el.option_name).join('、'))
    conditionsCount++
  }
  // 専門研修：プログラムの特徴
  if (query.feature_tag?.length > 0) {
    titleArr.push(
      store.state.master.featureTag
        .filter((el: Master) => query.feature_tag.includes(el.opt_id))
        .map((el: Master) => el.option_name)
        .join('、')
    )
    conditionsCount++
  }
  // 共通：救急指定
  if (query.designated_hospital && query.designated_hospital.length > 0) {
    const items = store.state.master.designatedHospital.filter((el: Master) => {
      return query.designated_hospital.includes(el.opt_id)
    })

    titleArr.push(items.map((el: Master) => el.option_name).join('、'))
    conditionsCount++
  }
  // 病院：こだわり条件 / 専門研修：病院の特徴
  if (query.discerning_conditions && query.discerning_conditions.length > 0) {
    const items = store.state.master.keywords.filter((el: Master) => {
      return query.discerning_conditions.includes(el.opt_id)
    })

    titleArr.push(items.map((el: Master) => el.option_name).join('、'))
    conditionsCount++
  }
  // 病院：その他希望の条件
  if (
    query.bonus ||
    query.overtime_hours_mavg ||
    query.house ||
    query.j_graduating_school
  ) {
    const values = []

    if (query.bonus) values.push('賞与あり')
    if (query.overtime_hours_mavg) values.push('時間外勤務あり')
    if (query.house) values.push('研修医のための宿舎あり')
    if (query.j_graduating_school) values.push('出身大学の先輩あり')

    titleArr.push(values.join('、'))
    conditionsCount++
  }
  // 共通: 病床数
  if (query.bed_num_lo || query.bed_num_hi) {
    let value = '病床数'
    value += query.bed_num_lo
      ? numberFormatAddComma(Number(query.bed_num_lo)) + '床以上'
      : ''
    value += query.bed_num_hi
      ? numberFormatAddComma(Number(query.bed_num_hi)) + '床未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 専門研修: 当科病床数
  if (query.department_bed_num_lo || query.department_bed_num_hi) {
    let value = '当科病床数'
    value += query.department_bed_num_lo
      ? numberFormatAddComma(Number(query.department_bed_num_lo)) + '床以上'
      : ''
    value += query.department_bed_num_hi
      ? numberFormatAddComma(Number(query.department_bed_num_hi)) + '床未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 専門研修: 当科在籍研修医数
  if (query.resident_num_lo || query.resident_num_hi) {
    let value = '当科在籍研修医数'
    value += query.resident_num_lo
      ? numberFormatAddComma(Number(query.resident_num_lo)) + '人以上'
      : ''
    value += query.resident_num_hi
      ? numberFormatAddComma(Number(query.resident_num_hi)) + '人未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 専門研修: 当科在籍指導医数
  if (query.instructor_num_lo || query.instructor_num_hi) {
    let value = '当科在籍指導医数'
    value += query.instructor_num_lo
      ? numberFormatAddComma(Number(query.instructor_num_lo)) + '人以上'
      : ''
    value += query.instructor_num_hi
      ? numberFormatAddComma(Number(query.instructor_num_hi)) + '人未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 共通: 医師数
  if (query.doctor_num_lo || query.doctor_num_hi) {
    let value = '医師数'
    value += query.doctor_num_lo
      ? numberFormatAddComma(Number(query.doctor_num_lo)) + '人以上'
      : ''
    value += query.doctor_num_hi
      ? numberFormatAddComma(Number(query.doctor_num_hi)) + '人未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 病院：募集定員
  if (query.positions_num_lo || query.positions_num_hi) {
    let value = '募集定員'
    value += query.positions_num_lo
      ? numberFormatAddComma(Number(query.positions_num_lo)) + '人以上'
      : ''
    value += query.positions_num_hi
      ? numberFormatAddComma(Number(query.positions_num_hi)) + '人未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 専門研修：募集定員（採用予定人数）
  if (query.recruitment_num_lo || query.recruitment_num_hi) {
    let value = '募集定員（採用予定人数）'
    value += query.recruitment_num_lo
      ? numberFormatAddComma(Number(query.recruitment_num_lo)) + '人以上'
      : ''
    value += query.recruitment_num_hi
      ? numberFormatAddComma(Number(query.recruitment_num_hi)) + '人未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 病院：年収
  if (query.income_lo || query.income_hi) {
    let value = '年収'
    value += query.income_lo
      ? numberFormatAddComma(Number(query.income_lo)) + '万円以上'
      : ''
    value += query.income_hi
      ? numberFormatAddComma(Number(query.income_hi)) + '万円未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 病院：当直回数
  if (query.shifts_num_lo || query.shifts_num_hi) {
    let value = '当直回数'
    value += query.shifts_num_lo
      ? numberFormatAddComma(Number(query.shifts_num_lo)) + '回以上'
      : ''
    value += query.shifts_num_hi
      ? numberFormatAddComma(Number(query.shifts_num_hi)) + '回未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 専門研修：年収
  if (query.s_salary3_num_lo || query.s_salary3_num_hi) {
    let value = '年収'
    value += query.s_salary3_num_lo
      ? numberFormatAddComma(Number(query.s_salary3_num_lo)) + '万円以上'
      : ''
    value += query.s_salary3_num_hi
      ? numberFormatAddComma(Number(query.s_salary3_num_hi)) + '万円未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }
  // 専門研修：当直回数
  if (query.s_shifts3_lo || query.s_shifts3_hi) {
    let value = '当直回数'
    value += query.s_shifts3_lo
      ? numberFormatAddComma(Number(query.s_shifts3_lo)) + '回以上'
      : ''
    value += query.s_shifts3_hi
      ? numberFormatAddComma(Number(query.s_shifts3_hi)) + '回未満'
      : ''

    titleArr.push(value)
    conditionsCount++
  }

  if (conditionsCount >= 5 || query.free_word) {
    return {
      title: '様々な条件の' + baseTitle,
      breadCrumbs: areaCondition
    }
  }

  return {
    title: titleArr.join('／') + 'の' + baseTitle
  }
}
